import {createApi} from '@reduxjs/toolkit/query/react'
import {baseQuery} from '../base-query'

export interface DirectoryContact {
  id: string
  name: string
  // Optional here because contacts created before this field existed have no title;
  // the UI treats a missing title as '-' and requires one on the next edit.
  title?: string
  email: string
  phone: string
  companyId: string
  siteId?: string
  siteName?: string
  departmentId?: string
  departmentName?: string
  archived?: boolean
  createdDate?: string
  modifiedDate?: string
}

export interface DirectoryContactPagingResponse {
  data: DirectoryContact[]
  pageNumber: number
  pageSize: number
  totalCount: number
  totalPages: number
}

export interface GetContactsByCompanyParams {
  companyId: string
  pageNumber?: number
  pageSize?: number
  search?: string
  siteId?: string
  departmentId?: string
  isArchived?: boolean
}

export interface GetContactsBySiteParams {
  siteId: string
  pageNumber?: number
  pageSize?: number
  search?: string
  departmentId?: string
  isArchived?: boolean
}

export interface CreateContactRequest {
  companyId: string
  name: string
  title: string
  email: string
  phone: string
  siteId?: string
  siteName?: string
  departmentId?: string
  departmentName?: string
}

export interface UpdateContactRequest {
  contactId: string
  name?: string
  title?: string
  email?: string
  phone?: string
  siteId?: string
  siteName?: string
  departmentId?: string
  departmentName?: string
}

export interface ArchiveContactRequest {
  contactId: string
  archivedBy: string
}

export const directoryContactApi = createApi({
  reducerPath: 'DirectoryContactApi',
  tagTypes: ['DirectoryContact'],
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    getContactsByCompany: builder.query<DirectoryContactPagingResponse, GetContactsByCompanyParams>(
      {
        providesTags: ['DirectoryContact'],
        query: ({
          companyId,
          pageNumber = 1,
          pageSize = 25,
          search,
          siteId,
          departmentId,
          isArchived,
        }) => ({
          url: `/directory-contacts/company/${companyId}`,
          method: 'GET',
          params: {
            pageNumber,
            pageSize,
            ...(search && {search}),
            ...(siteId && {siteId}),
            ...(departmentId && {departmentId}),
            ...(isArchived !== undefined && {isArchived: String(isArchived)}),
          },
        }),
      },
    ),
    getContactsBySite: builder.query<DirectoryContactPagingResponse, GetContactsBySiteParams>({
      providesTags: ['DirectoryContact'],
      query: ({siteId, pageNumber = 1, pageSize = 25, search, departmentId, isArchived}) => ({
        url: `/directory-contacts/site/${siteId}`,
        method: 'GET',
        params: {
          pageNumber,
          pageSize,
          ...(search && {search}),
          ...(departmentId && {departmentId}),
          ...(isArchived !== undefined && {isArchived: String(isArchived)}),
        },
      }),
    }),
    createContact: builder.mutation<DirectoryContact, CreateContactRequest>({
      invalidatesTags: ['DirectoryContact'],
      query: ({companyId, ...body}) => ({
        url: `/directory-contacts/company/${companyId}`,
        method: 'POST',
        body,
      }),
    }),
    updateContact: builder.mutation<DirectoryContact, UpdateContactRequest>({
      invalidatesTags: ['DirectoryContact'],
      query: ({contactId, ...body}) => ({
        url: `/directory-contacts/${contactId}`,
        method: 'PUT',
        body,
      }),
    }),
    archiveContact: builder.mutation<DirectoryContact, ArchiveContactRequest>({
      invalidatesTags: ['DirectoryContact'],
      query: ({contactId, archivedBy}) => ({
        url: `/directory-contacts/${contactId}/archive`,
        method: 'PUT',
        body: {archivedBy},
      }),
    }),
    reactivateContact: builder.mutation<DirectoryContact, string>({
      invalidatesTags: ['DirectoryContact'],
      query: (contactId) => ({
        url: `/directory-contacts/${contactId}/reactivate`,
        method: 'PUT',
      }),
    }),
  }),
})

export const {
  useGetContactsByCompanyQuery,
  useGetContactsBySiteQuery,
  useCreateContactMutation,
  useUpdateContactMutation,
  useArchiveContactMutation,
  useReactivateContactMutation,
} = directoryContactApi
